'use client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Hidden } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import useTheme from '@mui/material/styles/useTheme';
import { useAddOrUpdateCelebration } from '@/actions/feed.actions';
import useMediaQuery from '@mui/material/useMediaQuery';
import Dialog from '@ui/Dialog';
import Typography from '@ui/Typography';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { MdArrowBackIos } from 'react-icons/md';
import * as yup from 'yup';
import useStore from '../../stores/store';
import StyledTextButton from '../Styled/StyledTextButton';
import CelebrateDetailsForm from './celebrateDetailsForm';
import useCelebrateDialogStore from './celebrateDialog.store';
import CelebrateTemplateCard from './celebrateTemplateCard';
import ActivitySuccessDialog from '../ActivitySuccessDialog';
import { TargetViewers } from '@/constants/profileType.constant';
import { useBreakpointHooks } from '@/utils/break-points.provider';
import Button from '../next-ui/Button';

const templates = [
  {
    createdAt: '2022-08-01T04:10:33.989Z',
    created_by: 1,
    default_message:
      'A very happy birthday and wishing you a wonderful year ahead!',
    description: 'Happy Birthday template',
    id: 1,
    name: 'Happy Birthday',
    post_bg_resource_type: 'image',
    post_bg_resource_url: '/assets/images/celebrate-2.png',
    updatedAt: '2022-08-01T04:10:33.989Z',
  },
  {
    createdAt: '2022-08-01T04:10:33.989Z',
    created_by: 1,
    default_message: 'You added another year to your illustrious career!',
    description: 'Work Anniversary template',
    id: 2,
    name: 'Happy Work Anniversary',
    post_bg_resource_type: 'image',
    post_bg_resource_url: '/assets/images/celebrate-1.png',
    updatedAt: '2022-08-01T04:10:33.989Z',
  },
  {
    createdAt: '2022-08-01T04:10:33.989Z',
    created_by: 1,
    default_message: 'Kudos to you for your hard work!',
    description: 'Kudos template',
    id: 3,
    name: 'Kudos',
    post_bg_resource_type: 'image',
    post_bg_resource_url: '/assets/images/celebrate-3.png',
    updatedAt: '2022-08-01T04:10:33.989Z',
  },
];

const defaultFormValues = {
  recipients: [],
  message: '',
  imageUrl: '',
};

const formValidator = yup.object().shape({
  recipients: yup
    .array()
    .required('Please select at least one recipient')
    .min(1, 'Please select at least one recipient'),
  message: yup.string().required('Please enter a message'),
  imageUrl: yup.string().required('Please select an image'),
});

function CelebrateDialog(props) {
  const { onClose } = props;
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const userProfile = useStore((state) => state.user.profile);

  // celebration dialog store selectors
  const open = useCelebrateDialogStore((state) => state.open);
  const setOpen = useCelebrateDialogStore((state) => state.setOpen);
  const stepNum = useCelebrateDialogStore((state) => state.stepNum);
  const setStepNum = useCelebrateDialogStore((state) => state.setStepNum);
  const selectedTemplate = useCelebrateDialogStore((state) => state.template);
  const selectedPost = useCelebrateDialogStore((state) => state.post);
  const setSelectedTemplate = useCelebrateDialogStore(
    (state) => state.setSelectedTemplate,
  );

  const isLessThanSmallScreen = useBreakpointHooks((bp) => bp.useSmaller('sm'));

  // const { data: celebTemplates } = useQuery({
  //   queryKey: ['celebration-templates'],
  //   queryFn: async () => {
  //     const { ok, data } = await fetchViaProxy(
  //       '/proxy/v1/celebration-templates',
  //     );
  //     if (ok) {
  //       return data.data;
  //     }
  //     throw new Error('Unable to fetch celebration templates');
  //   },
  //   enabled: open,
  //   staleTime: Infinity,
  //   refetchInterval: Infinity,
  //   refetchOnMount: true,
  //   refetchOnReconnect: false,
  //   refetchOnWindowFocus: false,
  // });

  // console.log('celebTemplates', celebTemplates);

  // const templates = celebTemplates || [];
  const isEditMode = !!selectedPost;

  const [rewardEarned, setRewardEarned] = useState(0);
  const [createdPost, setCreatedPost] = useState(null);

  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(-1);

  const [saving, setSaving] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);

  const formMethods = useForm({
    mode: 'onChange',
    defaultValues: defaultFormValues,
    resolver: yupResolver(formValidator),
  });

  const {
    mutateAsync: addOrUpdateCelebration,
    isSuccess,
    isError,
  } = useAddOrUpdateCelebration();

  let headerText = 'Pick your celebration';
  if (stepNum === 1) {
    if (isEditMode) {
      headerText = 'Edit celebration';
    } else {
      headerText = selectedTemplate ? selectedTemplate.name : 'Enter details';
    }
  } else if (stepNum === 2) {
    headerText = '';
  }

  useEffect(() => {
    if (saving) {
      if (isSuccess) {
        setStepNum(2);
        setSaving(false);
      } else if (isError) {
        setSaving(false);
      }
    }
  }, [isSuccess, isError, saving]);

  const closeDialog = () => {
    setOpen(false);
    setSelectedTemplateIndex(-1);
    formMethods.reset(defaultFormValues);
    onClose(stepNum === 2);
  };

  const handleBack = () => {
    if (isEditMode) {
      closeDialog();
    } else {
      setStepNum(stepNum - 1);
    }
  };

  const handleClose = () => {
    if (stepNum === 1) {
      handleBack();
    } else {
      closeDialog();
    }
  };

  const handlePost = async () => {
    if (formMethods.formState.isValid) {
      setSaving(true);
      const formData = formMethods.getValues();
      const payload = {
        message: formData.message,
        attachmentUrl: formData.imageUrl,
        attachmentType: 'image',
        recipients: formData.recipients.map((recipient) => ({
          id: recipient.id,
          recipientType: 'user',
        })),
      };

      if (!isEditMode) {
        payload.celebrationType = templates[selectedTemplateIndex].name;
      }

      const data = await addOrUpdateCelebration({
        formValues: payload,
        files: selectedFile,
        post: selectedPost,
      });
      setRewardEarned(data?.reward);
      setCreatedPost(data?.post);
    } else {
      formMethods.trigger();
    }
  };

  const handleTemplateSelection = (index) => {
    if (index >= 0) {
      setSelectedTemplateIndex(index);
      setSelectedTemplate(templates[index]);
    }
  };

  let contentElement;
  let actionElement;

  switch (stepNum) {
    case 0:
      contentElement = (
        <div className="flex w-full flex-1 flex-col gap-2">
          {templates?.map((item, index) => (
            <div
              key={item.id}
              role="button"
              onClick={() => handleTemplateSelection(index)}
              onKeyDown={() => handleTemplateSelection(index)}
              tabIndex={0}>
              <CelebrateTemplateCard
                isSelected={selectedTemplateIndex === index}
                template={item}
              />
            </div>
          ))}
        </div>
      );

      actionElement = <DialogActions />;
      break;
    // details for
    case 1:
      contentElement = (
        <FormProvider {...formMethods}>
          <CelebrateDetailsForm
            template={selectedTemplate}
            post={selectedPost}
            setSelectedFile={setSelectedFile}
            selectedFile={selectedFile}
          />
        </FormProvider>
      );
      actionElement = (
        <div
          className={`flex-rows flex justify-end gap-12 pb-2 ${
            isLessThanSmallScreen ? 'mb-32' : ''
          }`}>
          {/* <div className="flex gap-2">
            {!isEditMode && (
              <StyledContainedButton
                onClick={handleBack}
                disabled={saving}
                invertColors
                type="button">
                Back
              </StyledContainedButton>
            )}

            <StyledContainedButton
              loading={saving}
              type="button"
              onClick={handlePost}>
              {isEditMode ? 'Save' : 'Post'}
            </StyledContainedButton>
          </div> */}

          <div className="flex gap-2">
            {!isEditMode && (
              <Button variant="outlined" onClick={handleBack} disabled={saving}>
                Back
              </Button>
            )}

            <Button loading={saving} onClick={handlePost}>
              {isEditMode ? 'Save' : 'Post'}
            </Button>
          </div>
        </div>
      );
      break;
    case 2:
      contentElement = (
        <ActivitySuccessDialog
          user={userProfile}
          onClose={handleClose}
          reward={rewardEarned}
          post={createdPost}
          isPostPublic={createdPost?.targetViewers === TargetViewers.All}
          isPost
        />
      );
      actionElement = <></>;
      break;
    default:
      break;
  }

  return (
    <Dialog
      isOpen={open}
      onClose={closeDialog}
      maxWidth="lg"
      title={headerText}>
      <div className="flex w-full flex-col gap-12 p-0 lg:p-0">
        <div className="hidden items-center justify-between">
          {stepNum === 1 && (
            <Hidden mdUp>
              <StyledTextButton type="button" onClick={handleBack}>
                <MdArrowBackIos />
              </StyledTextButton>
            </Hidden>
          )}

          <div className="flex items-center justify-center text-center">
            <Typography variant="h2" className="uppercase text-chips-primary">
              {headerText}
            </Typography>
          </div>

          {stepNum === 1 && (
            <Hidden mdUp>
              <StyledTextButton type="button" onClick={handlePost}>
                {isEditMode ? 'Save' : 'Post'}
              </StyledTextButton>
            </Hidden>
          )}
          {stepNum !== 1 && (
            <Hidden mdUp>
              <StyledTextButton type="button" onClick={handleClose}>
                Close
              </StyledTextButton>
            </Hidden>
          )}
        </div>
        <div className="flex w-full flex-col items-start justify-start overflow-y-auto pr-4">
          {contentElement}
        </div>
        {actionElement}
      </div>
    </Dialog>
  );
}

export default CelebrateDialog;
